import React from 'react'
import {
  QuestionType,
  QuestionRenderer,
  Question,
  TextAnswer,
  EmailAnswer,
  DateAnswer,
} from './types'

// Import individual renderer components
import { ShortTextRecentList } from './short-text-recent-list'
import { LongTextRecentList } from './long-text-recent-list'
import { EmailOmittedNote } from './email-omitted-note'
import { DropdownPie } from './dropdown-pie'
import { BirthdayAgeBucketsBar } from './birthday-age-buckets-bar'
import { PictureChoiceBars } from './picture-choice-bars'
import { MultiSelectSemiDonut } from './multi-select-semi-donut'
import { GenderVerticalBars } from './gender-vertical-bars'
import { CheckboxPercentBar } from './checkbox-percent-bar'

// Registry mapping question types to their renderer components
export const QuestionRendererRegistry: Record<QuestionType, QuestionRenderer> =
  {
    // Added generic "text" mapping (alias of short_text)
    text: {
      component: ShortTextRecentList,
      transforms: (question) => ({
        answers: question.answers
          .filter((a): a is TextAnswer | EmailAnswer => 'submittedAt' in a)
          .sort(
            (a, b) =>
              new Date(b.submittedAt).getTime() -
              new Date(a.submittedAt).getTime(),
          )
          .slice(0, 5),
      }),
    },

    short_text: {
      component: ShortTextRecentList,
      transforms: (question) => ({
        answers: question.answers
          .filter((a): a is TextAnswer | EmailAnswer => 'submittedAt' in a)
          .sort(
            (a, b) =>
              new Date(b.submittedAt).getTime() -
              new Date(a.submittedAt).getTime(),
          )
          .slice(0, 5),
      }),
    },

    // Added generic "textarea" mapping (alias of long_text)
    textarea: {
      component: LongTextRecentList,
      transforms: (question) => ({
        answers: question.answers
          .filter((a): a is TextAnswer | EmailAnswer => 'submittedAt' in a)
          .sort(
            (a, b) =>
              new Date(b.submittedAt).getTime() -
              new Date(a.submittedAt).getTime(),
          )
          .slice(0, 3),
      }),
    },

    long_text: {
      component: LongTextRecentList,
      transforms: (question) => ({
        answers: question.answers
          .filter((a): a is TextAnswer | EmailAnswer => 'submittedAt' in a)
          .sort(
            (a, b) =>
              new Date(b.submittedAt).getTime() -
              new Date(a.submittedAt).getTime(),
          )
          .slice(0, 3),
      }),
    },

    email: {
      component: EmailOmittedNote,
    },

    dropdown: {
      component: DropdownPie,
      defaultDenominator: 'respondents',
    },

    // Added "shadcn-select" mapping (reusing dropdown visualization)
    'shadcn-select': {
      component: DropdownPie,
      defaultDenominator: 'respondents',
    },

    date: {
      component: BirthdayAgeBucketsBar,
      transforms: (question) => ({
        ageBuckets: generateAgeBuckets(question.answers as DateAnswer[]),
      }),
      defaultDenominator: 'respondents',
    },

    picture_choice: {
      component: PictureChoiceBars,
      defaultDenominator: 'respondents',
    },

    dynamic_radio_multi: {
      component: MultiSelectSemiDonut,
      defaultDenominator: 'respondents',
    },

    gender_radio: {
      component: GenderVerticalBars,
      transforms: (question) => ({
        normalizedGenders: normalizeGenderResponses(
          question.aggregates?.buckets || [],
        ),
      }),
      defaultDenominator: 'respondents',
    },

    checkbox: {
      component: CheckboxPercentBar,
      defaultDenominator: 'respondents',
    },

    // Added "switch" mapping (reuse checkbox percent bar for on/off)
    switch: {
      component: CheckboxPercentBar,
      defaultDenominator: 'respondents',
    },
  }

// Helper function to generate age buckets from date answers
function generateAgeBuckets(
  answers: DateAnswer[],
): Array<{ label: string; count: number }> {
  const buckets = new Map<string, number>()
  const today = new Date()

  answers.forEach((answer) => {
    try {
      const birthDate = new Date(answer.value)
      const age = Math.floor(
        (today.getTime() - birthDate.getTime()) /
          (365.25 * 24 * 60 * 60 * 1000),
      )

      let bucketKey: string
      if (age < 0 || isNaN(age)) {
        bucketKey = 'Unknown'
      } else if (age >= 100) {
        bucketKey = '100+'
      } else {
        const bucketStart = Math.floor(age / 10) * 10
        bucketKey = `${bucketStart}–${bucketStart + 9}`
      }

      buckets.set(bucketKey, (buckets.get(bucketKey) || 0) + 1)
    } catch {
      buckets.set('Unknown', (buckets.get('Unknown') || 0) + 1)
    }
  })

  // Convert to sorted array
  const result = Array.from(buckets.entries()).map(([label, count]) => ({
    label,
    count,
  }))

  // Sort age buckets properly (0-9, 10-19, ..., 100+, Unknown)
  return result.sort((a, b) => {
    if (a.label === 'Unknown') return 1
    if (b.label === 'Unknown') return -1
    if (a.label === '100+') return b.label === 'Unknown' ? -1 : 1
    if (b.label === '100+') return a.label === 'Unknown' ? 1 : -1

    const aStart = parseInt(a.label.split('–')[0])
    const bStart = parseInt(b.label.split('–')[0])
    return aStart - bStart
  })
}

// Helper function to normalize gender responses to standard taxonomy
function normalizeGenderResponses(
  buckets: Array<{ label: string; value?: string; count: number }>,
) {
  const genderMap = new Map<string, number>()

  // Standard order for gender display (matching actual form options)
  const standardOrder = ['Man', 'Woman', 'Different']

  const matchesTokens = (tokens: string[], candidates: string[]) =>
    tokens.some((token) => candidates.includes(token))

  buckets.forEach((bucket) => {
    const rawValue = (bucket.value ?? bucket.label ?? '')
      .toString()
      .trim()
      .toLowerCase()

    if (!rawValue) {
      genderMap.set(
        'Different',
        (genderMap.get('Different') || 0) + bucket.count,
      )
      return
    }

    const tokenized = rawValue
      .replace(/[^a-z]+/g, ' ')
      .split(' ')
      .map((token) => token.trim())
      .filter(Boolean)

    const normalizedLabel = (() => {
      if (matchesTokens(tokenized, ['man', 'male', 'men'])) {
        return 'Man'
      }

      if (matchesTokens(tokenized, ['woman', 'women', 'female', 'vrouw'])) {
        return 'Woman'
      }

      if (
        matchesTokens(tokenized, [
          'different',
          'anders',
          'diverse',
          'divers',
          'other',
        ]) ||
        rawValue.includes('non-binary') ||
        rawValue.includes('nonbinary')
      ) {
        return 'Different'
      }

      return 'Different'
    })()

    genderMap.set(
      normalizedLabel,
      (genderMap.get(normalizedLabel) || 0) + bucket.count,
    )
  })

  return standardOrder.map((gender) => ({
    label: gender,
    count: genderMap.get(gender) || 0,
  }))
}

// Main function to get renderer for a question type
// eslint-disable-next-line react-refresh/only-export-components
export function getQuestionRenderer(
  questionType: QuestionType,
): QuestionRenderer | null {
  return QuestionRendererRegistry[questionType] || null
}

// Function to render a question with the appropriate component
// eslint-disable-next-line react-refresh/only-export-components
export function renderQuestion(
  question: Question,
  campaignId: string,
): React.ReactElement | null {
  const renderer = getQuestionRenderer(question.type)
  if (!renderer) return null

  const Component = renderer.component
  const transformedData = renderer.transforms
    ? renderer.transforms(question)
    : {}

  return (
    <Component
      question={{ ...question, ...transformedData }}
      campaignId={campaignId}
    />
  )
}
